import { useState, useEffect } from 'react';
import { getAll, add, update, remove, STORES } from '../db';
import { Plus, Trash2, Check, AlertCircle, Thermometer, Save } from 'lucide-react';

export function TodoList() {
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [priority, setPriority] = useState('normal');

  useEffect(() => { load(); }, []);
  const load = async () => {
    const data = await getAll(STORES.todos);
    setTodos(data.sort((a, b) => {
      const order = { high: 0, normal: 1, low: 2 };
      return (order[a.priority] || 1) - (order[b.priority] || 1);
    }));
  };

  const addTodo = async () => {
    if (!newTodo.trim()) return;
    await add(STORES.todos, { text: newTodo, priority, done: false, createdAt: Date.now() });
    setNewTodo('');
    load();
  };

  const toggleDone = async (todo) => {
    await update(STORES.todos, { ...todo, done: !todo.done });
    load();
  };

  const deleteTodo = async (id) => {
    await remove(STORES.todos, id);
    load();
  };

  const pending = todos.filter(t => !t.done);
  const done = todos.filter(t => t.done);

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div><h2 className="section-title">To-Do List</h2><p className="section-sub">{pending.length} outstanding</p></div>
      </div>
      <div className="todo-input-row">
        <input value={newTodo} onChange={e => setNewTodo(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTodo()} placeholder="Add a task..." className="search-input" style={{ flex: 1 }} />
        <select value={priority} onChange={e => setPriority(e.target.value)} className="filter-select">
          <option value="high">🔴 High</option>
          <option value="normal">🟡 Normal</option>
          <option value="low">🟢 Low</option>
        </select>
        <button className="btn-gold" onClick={addTodo}><Plus size={14} /> Add</button>
      </div>
      <div className="todo-list">
        {pending.map(t => (
          <div key={t.id} className={`todo-item priority-${t.priority}`}>
            <button className="todo-check" onClick={() => toggleDone(t)}><Check size={14} /></button>
            <span className="todo-text">{t.text}</span>
            <span className={`priority-dot ${t.priority}`}></span>
            <button className="icon-btn danger" onClick={() => deleteTodo(t.id)}><Trash2 size={13} /></button>
          </div>
        ))}
        {pending.length === 0 && <div className="empty-state"><p>All done! 🎉</p></div>}
        {done.length > 0 && (
          <>
            <div className="done-header">Completed ({done.length})</div>
            {done.map(t => (
              <div key={t.id} className="todo-item done">
                <button className="todo-check done" onClick={() => toggleDone(t)}><Check size={14} /></button>
                <span className="todo-text done">{t.text}</span>
                <button className="icon-btn danger" onClick={() => deleteTodo(t.id)}><Trash2 size={13} /></button>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export function FridgeCheck() {
  const [checks, setChecks] = useState([]);
  const [form, setForm] = useState({ current: '', highest: '', lowest: '', reset: 'No', notes: '' });
  const [saved, setSaved] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const data = await getAll(STORES.fridgeChecks);
    setChecks(data.sort((a, b) => b.createdAt - a.createdAt).slice(0, 30));
  };

  const todayCheck = checks.find(c => new Date(c.createdAt).toDateString() === new Date().toDateString());

  const saveCheck = async () => {
    if (!form.current) return;
    await add(STORES.fridgeChecks, { ...form, date: new Date().toISOString() });
    setSaved(true);
    setForm({ current: '', highest: '', lowest: '', reset: 'No', notes: '' });
    setTimeout(() => setSaved(false), 2000);
    load();
  };

  const tempStatus = (temp) => {
    const t = parseFloat(temp);
    if (isNaN(t)) return '';
    if (t >= 2 && t <= 8) return 'good';
    if (t >= 1 && t <= 10) return 'ok';
    return 'bad';
  };

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div><h2 className="section-title">Fridge Check</h2><p className="section-sub">Medical fridge temperature log</p></div>
      </div>

      {todayCheck ? (
        <div className="fridge-done-banner">
          <Check size={16} /> Today's fridge check completed ✓
        </div>
      ) : (
        <div className="fridge-alert-banner">
          <AlertCircle size={16} /> Fridge check not yet completed today
        </div>
      )}

      <div className="fridge-form">
        <div className="form-section-title">Today's Check — {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
        <div className="form-grid">
          <div className="form-field">
            <label>Current Temp (°C) *</label>
            <input type="number" step="0.1" value={form.current} onChange={e => setForm({ ...form, current: e.target.value })} placeholder="e.g. 4.5" className={tempStatus(form.current)} />
          </div>
          <div className="form-field">
            <label>Highest Temp (°C)</label>
            <input type="number" step="0.1" value={form.highest} onChange={e => setForm({ ...form, highest: e.target.value })} />
          </div>
          <div className="form-field">
            <label>Lowest Temp (°C)</label>
            <input type="number" step="0.1" value={form.lowest} onChange={e => setForm({ ...form, lowest: e.target.value })} />
          </div>
          <div className="form-field">
            <label>Reset Required?</label>
            <select value={form.reset} onChange={e => setForm({ ...form, reset: e.target.value })}>
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>
          <div className="form-field full">
            <label>Notes</label>
            <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any issues to note..." />
          </div>
        </div>
        <button className="btn-gold" onClick={saveCheck} style={{ marginTop: '1rem' }}>
          {saved ? <><Check size={14} /> Saved!</> : <><Save size={14} /> Log Check</>}
        </button>
      </div>

      <div style={{ marginTop: '2rem' }}>
        <div className="form-section-title">Recent Checks</div>
        <div className="fridge-log">
          {checks.map(c => (
            <div key={c.id} className="fridge-log-row">
              <span className="fridge-date">{new Date(c.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
              <span className={`fridge-temp ${tempStatus(c.current)}`}><Thermometer size={12} /> {c.current}°C</span>
              {c.highest && <span className="fridge-range">H: {c.highest}°C</span>}
              {c.lowest && <span className="fridge-range">L: {c.lowest}°C</span>}
              <span className="fridge-reset">{c.reset === 'Yes' ? '⚠ Reset' : ''}</span>
              <span className="fridge-notes">{c.notes}</span>
            </div>
          ))}
          {checks.length === 0 && <div className="empty-state"><p>No checks recorded yet</p></div>}
        </div>
      </div>
    </div>
  );
}

export function StockList() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => { load(); }, []);
  const load = async () => {
    const data = await getAll(STORES.products);
    setProducts(data.sort((a, b) => a.name?.localeCompare(b.name)));
  };

  const updateStock = async (product, qty) => {
    await update(STORES.products, { ...product, stockQty: qty });
    load();
  };

  const isLow = (p) => p.reorderPoint && parseInt(p.stockQty) <= parseInt(p.reorderPoint);
  const isExpiringSoon = (p) => {
    if (!p.expiryDate) return false;
    const days = (new Date(p.expiryDate) - Date.now()) / (1000 * 60 * 60 * 24);
    return days < 90;
  };

  const filtered = products.filter(p => {
    if (filter === 'low') return isLow(p);
    if (filter === 'expiring') return isExpiringSoon(p);
    return true;
  });

  const lowCount = products.filter(isLow).length;
  const expiringCount = products.filter(isExpiringSoon).length;

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div><h2 className="section-title">Stock</h2><p className="section-sub">{products.length} products tracked</p></div>
      </div>

      <div className="stock-filter-tabs">
        <button className={`stock-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All Stock</button>
        <button className={`stock-tab ${filter === 'low' ? 'active' : ''}`} onClick={() => setFilter('low')}>
          Low Stock {lowCount > 0 && <span className="alert-badge">{lowCount}</span>}
        </button>
        <button className={`stock-tab ${filter === 'expiring' ? 'active' : ''}`} onClick={() => setFilter('expiring')}>
          Expiring Soon {expiringCount > 0 && <span className="alert-badge">{expiringCount}</span>}
        </button>
      </div>

      <div className="stock-list">
        <div className="stock-list-header">
          <span>Product</span><span>Supplier</span><span>Qty in Stock</span><span>Expiry</span><span>Status</span>
        </div>
        {filtered.map(p => (
          <div key={p.id} className={`stock-row ${isLow(p) ? 'low-stock' : ''} ${isExpiringSoon(p) ? 'expiring' : ''}`}>
            <div>
              <div className="product-name">{p.name}</div>
              <div className="product-size">{p.brand} · {p.size}</div>
            </div>
            <div className="product-size">{p.supplier || '—'}</div>
            <div>
              <input type="number" min="0" value={p.stockQty || ''} onChange={e => updateStock(p, e.target.value)}
                className="stock-qty-input" placeholder="0" />
              {p.reorderPoint && <div className="reorder-note">Reorder at {p.reorderPoint}</div>}
            </div>
            <div className="product-size">{p.expiryDate ? new Date(p.expiryDate).toLocaleDateString('en-GB') : '—'}</div>
            <div>
              {isLow(p) && <span className="status-badge low">Low</span>}
              {isExpiringSoon(p) && <span className="status-badge expiring">Expiring</span>}
              {!isLow(p) && !isExpiringSoon(p) && <span className="status-badge ok">OK</span>}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="empty-state"><p>Nothing to show here 🎉</p></div>}
      </div>
    </div>
  );
}
